import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import {
  MOCK_BATCHES_BY_ITEM,
  MOCK_BRANCHES,
  MOCK_ITEMS,
  MOCK_WAREHOUSES,
  mockStockAtDestination,
} from '../mockData'
import { getReceipt, upsertReceipt } from '../storage'
import type { InventoryReceived, InventoryReceivedLine, InventoryReceivedStatus } from '../types'
import { newReceivedLine } from '../types'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function clampNonNegative(n: number) {
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

function balanceAfterReceive(line: { previousQuantity?: number; quantityReceived?: number }) {
  const prev = Number(line.previousQuantity || 0)
  const recv = Number(line.quantityReceived || 0)
  return clampNonNegative(prev + recv)
}

function ensureFiveLines(lines: InventoryReceivedLine[]) {
  if (lines.length >= 5) return lines
  return [...lines, ...Array.from({ length: 5 - lines.length }, () => newReceivedLine())]
}

function warehouseLabel(id: string): string {
  const w = MOCK_WAREHOUSES.find((x) => String(x.id) === String(id))
  if (!w) return id
  return `${w.id} — ${[w.code, w.name].filter(Boolean).join(' — ')}`
}

function normalizeStatus(raw: unknown): InventoryReceivedStatus {
  const s = String(raw)
  if (s === 'pending' || s === 'received' || s === 'inspected' || s === 'cancelled') return s
  return 'pending'
}

export const InventoryReceivedEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isNew = !id || id === 'new'
  const existing = useMemo(() => (!isNew && id ? getReceipt(id) : undefined), [id, isNew])

  const [receivedDate, setReceivedDate] = useState(existing?.receivedDate || todayIso())
  const [sourceWarehouseId, setSourceWarehouseId] = useState(existing?.sourceWarehouseId || '')
  const [destinationBranchId, setDestinationBranchId] = useState(existing?.destinationBranchId || '')
  const [destinationWarehouseId, setDestinationWarehouseId] = useState(existing?.destinationWarehouseId || '')
  const [remarks, setRemarks] = useState(existing?.remarks || '')
  const [status, setStatus] = useState<InventoryReceivedStatus>(() => normalizeStatus(existing?.status))
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [lines, setLines] = useState<InventoryReceivedLine[]>(() => ensureFiveLines(existing?.lines || []))

  const code = existing?.code || (isNew ? `REC-${Date.now()}` : '')
  const receiptId = existing?.id || (isNew ? `${Date.now()}` : String(id))

  const destWhId = destinationWarehouseId

  const destinationWarehouses = useMemo(() => {
    const bid = destinationBranchId
    if (!bid) return []
    return MOCK_WAREHOUSES.filter((w) => String(w.branchId) === String(bid))
  }, [destinationBranchId])

  const itemOptions = useMemo(() => {
    return MOCK_ITEMS.map((it) => ({
      value: String(it.id),
      label: `${it.code} — ${it.name}`,
    }))
  }, [])

  const handleDestinationBranchChange = (value: string) => {
    setDestinationBranchId(value)
    setDestinationWarehouseId('')
    setLines(ensureFiveLines([]))
  }

  const handleSourceWarehouseChange = (v: string) => {
    setSourceWarehouseId(v)
    setLines(ensureFiveLines([]))
  }

  const handleDestinationWarehouseChange = (v: string) => {
    setDestinationWarehouseId(v)
    setLines(ensureFiveLines([]))
  }

  const addLines = (n: number) => setLines((prev) => [...prev, ...Array.from({ length: n }, () => newReceivedLine())])
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index))
  const updateLine = (index: number, patch: Partial<InventoryReceivedLine>) => {
    setLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const handleItemChange = (index: number, value: string) => {
    const line = lines[index]
    if (!line) return
    updateLine(index, {
      itemId: value,
      batchId: '',
      previousQuantity: 0,
      quantityReceived: 0,
      balanceQuantity: 0,
    })
  }

  const handleBatchChange = (index: number, value: string) => {
    const line = lines[index]
    if (!line) return
    const prevQty = mockStockAtDestination(destWhId, line.itemId, value)
    const recv = Number(line.quantityReceived || 0)
    const nextRow: InventoryReceivedLine = {
      ...line,
      batchId: value,
      previousQuantity: prevQty,
      quantityReceived: recv,
      balanceQuantity: balanceAfterReceive({ previousQuantity: prevQty, quantityReceived: recv }),
    }
    updateLine(index, nextRow)
  }

  const handleQtyChange = (index: number, raw: string) => {
    const qty = Number(raw)
    const line = lines[index]
    if (!line) return
    const q = Number.isFinite(qty) ? qty : 0
    updateLine(index, {
      quantityReceived: q,
      balanceQuantity: balanceAfterReceive({ ...line, quantityReceived: q }),
    })
  }

  const handleBack = () => navigate('/inventory/received')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!sourceWarehouseId || !destinationWarehouseId) {
      toast.error(t('inventoryReceived.validationWarehouses'))
      return
    }
    if (!destinationBranchId) {
      toast.error(t('inventoryReceived.validationDestinationBranch'))
      return
    }
    if (sourceWarehouseId === destinationWarehouseId) {
      toast.error(t('inventoryReceived.validationDifferentWarehouses'))
      return
    }

    const filled = lines.filter((l) => l.itemId && l.batchId && Number(l.quantityReceived || 0) > 0)
    if (filled.length === 0) {
      toast.error(t('inventoryReceived.validationLines'))
      return
    }

    const srcWh = MOCK_WAREHOUSES.find((w) => String(w.id) === String(sourceWarehouseId))
    const destWh = MOCK_WAREHOUSES.find((w) => String(w.id) === String(destinationWarehouseId))

    const localPayload: InventoryReceived = {
      id: receiptId,
      code,
      receivedDate,
      sourceWarehouseId,
      destinationWarehouseId,
      destinationBranchId,
      sourceWarehouseName: srcWh ? warehouseLabel(srcWh.id) : undefined,
      destinationWarehouseName: destWh ? warehouseLabel(destWh.id) : undefined,
      remarks,
      status,
      attachmentName: attachmentFile?.name ?? existing?.attachmentName,
      lines: filled.map((l) => ({
        ...l,
        previousQuantity: Number(l.previousQuantity || 0),
        quantityReceived: Number(l.quantityReceived || 0),
        balanceQuantity: Number(l.balanceQuantity || 0),
      })),
    }

    upsertReceipt(localPayload)
    await showSaveSuccess(!isNew, t('inventoryReceived.saveSuccess'))
    navigate('/inventory/received')
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  const linesNeedDestination = !destWhId

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryReceived.formTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryReceived.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryReceived.sectionReceiptDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="received-date">{t('inventoryReceived.receivedDate')}</Label>
              <Input id="received-date" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryReceived.sourceWarehouse')}</Label>
              <Select value={sourceWarehouseId || undefined} onValueChange={handleSourceWarehouseChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryReceived.selectSource')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_WAREHOUSES.map((w) => (
                    <SelectItem key={w.id} value={String(w.id)}>
                      {w.code ? `${w.code} — ${w.name}` : w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryReceived.destinationBranch')}</Label>
              <Select value={destinationBranchId || undefined} onValueChange={handleDestinationBranchChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryReceived.selectDestinationBranch')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_BRANCHES.map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryReceived.destinationWarehouse')}</Label>
              <Select
                value={destinationWarehouseId || undefined}
                onValueChange={handleDestinationWarehouseChange}
                disabled={!destinationBranchId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryReceived.selectDestination')} />
                </SelectTrigger>
                <SelectContent>
                  {destinationWarehouses
                    .filter((w) => String(w.id) !== String(sourceWarehouseId))
                    .map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.code ? `${w.code} — ${w.name}` : w.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryReceived.file')}</Label>
              <Input type="file" onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)} aria-label="File" />
              <div className="text-xs text-muted-foreground">
                {attachmentFile ? attachmentFile.name : t('inventoryReceived.noFileChosen')}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryReceived.status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(normalizeStatus(v))}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryReceived.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('inventoryReceived.statusPending')}</SelectItem>
                  <SelectItem value="received">{t('inventoryReceived.statusReceived')}</SelectItem>
                  <SelectItem value="inspected">{t('inventoryReceived.statusInspected')}</SelectItem>
                  <SelectItem value="cancelled">{t('inventoryReceived.statusCancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="remarks">{t('inventoryReceived.remarks')}</Label>
              <Input id="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryReceived.sectionReceivedItems')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)} disabled={linesNeedDestination}>
                <Plus className="mr-1 h-4 w-4" />
                {t('inventoryReceived.addRow')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)} disabled={linesNeedDestination}>
                {t('inventoryReceived.add3Rows')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)} disabled={linesNeedDestination}>
                {t('inventoryReceived.add5Rows')}
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryReceived.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryReceived.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryReceived.colCurrentQty')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryReceived.colQtyReceived')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryReceived.colBalanceQty')}</TableHead>
                    <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>{t('common.delete')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const batchOptions = line.itemId ? MOCK_BATCHES_BY_ITEM[line.itemId] || [] : []
                    return (
                      <TableRow key={line.id}>
                        <TableCell className="align-top py-2">
                          <Select
                            value={line.itemId || undefined}
                            onValueChange={(v) => handleItemChange(index, v)}
                            disabled={linesNeedDestination}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue
                                placeholder={
                                  linesNeedDestination ? t('inventoryReceived.selectDestinationFirst') : t('inventoryReceived.selectItem')
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {itemOptions.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell className="align-top py-2">
                          <Select
                            value={line.batchId || undefined}
                            onValueChange={(v) => handleBatchChange(index, v)}
                            disabled={!line.itemId}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={t('inventoryReceived.selectBatch')} />
                            </SelectTrigger>
                            <SelectContent>
                              {batchOptions.map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>
                                  {b.code ? `${b.code} — ${b.reference}` : b.reference}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className="h-9 bg-muted/50 text-center"
                            value={line.itemId && line.batchId ? String(line.previousQuantity ?? 0) : ''}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            type="number"
                            min={0}
                            className="h-9 text-center"
                            value={line.batchId ? String(line.quantityReceived ?? 0) : ''}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            disabled={!line.batchId}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className="h-9 bg-muted/50 text-center"
                            value={line.batchId ? String(line.balanceQuantity ?? 0) : ''}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => removeLine(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" />
            {!isNew ? t('common.update') : t('inventoryReceived.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
